import { ContentsResponse, GetMixedContentsOptions, MixedContentsResponse } from "@robe/core-api";
import { Content, PickContentValues } from "@robe/core-types";
import { XcoreCore } from "XcoreCore";
import { mapFetchedContentRelationsToContent } from "./mapFetchedContentRelationsToContent";
import {
  ContentUnfetchedValuesFieldsObject,
  ContentUnfetchedValuesFieldsObjectResolved,
  resolveContentUnfetchedValuesFieldsIds,
} from "./resolveUnfetchedFields";

export * from "./resolveUnfetchedFields";

type FetchContentRelationsProps = {
  core: XcoreCore;
  relationSourceFields: ContentUnfetchedValuesFieldsObject[];
  options?: Omit<GetMixedContentsOptions<{}>, "fields"> & {
    fields?: string[];
  };
  subRelations?: Omit<FetchContentRelationsProps, "core">;
};

/**
 * Fetches and maps the relations for a single content.
 * It can also be used in `singleById` or `singletonByType` queries.
 *
 * @template T - The type of the content object, which defaults to `Content`.
 *
 * @param core - The XcoreCore instance used for fetching content.
 * @param relationSourceFields - The values fields on the content that represent unfetched relations.
 * If the target relation field is in list object this field needs to be specified in `ofListObjectField`.
 * @param options - Optional parameters for fetching content extending from `GetMixedContentsOptions`.
 * @param subRelations - Optional recursive fetch settings for deeper nested relations.
 *
 * @returns A function that fetches the relations for a given content, or returns `null` if the content is `null`.
 */
export const fetchRelationsForSingleContent =
  <T extends Content = Content>({ core, relationSourceFields, options, subRelations }: FetchContentRelationsProps) =>
    async (content: Content | null): Promise<T | null> => {
      if (!content) {
        return content;
      }

      const relationsIdsObjects = resolveContentUnfetchedValuesFieldsIds({
        content: content,
        fields: relationSourceFields,
      });

      // Prevent fetch if no IDs were found
      const uniqueRelationsIds = [...new Set(relationsIdsObjects.map(({ id }) => id))];
      if (uniqueRelationsIds.length === 0) {
        return content as T;
      }

      const fetchedRelationsContentsObject = await core.content.listByIds(uniqueRelationsIds, {
        includeNonLocalized: true,
        perPage: 10000,
        ...options,
      });

      let fetchedRelationsContents: Content[] = fetchedRelationsContentsObject.contents;

      if (subRelations) {
        const fetchedRelationsContentsSubRelations = await fetchRelationsForContents({ core, ...subRelations })(fetchedRelationsContents);
        if (fetchedRelationsContentsSubRelations) {
          fetchedRelationsContents = fetchedRelationsContentsSubRelations;
        }
      }

      const mappedRelationsContent = mapFetchedContentRelationsToContent({
        fetchedRelationsContents,
        relationSourceFields,
      })(content);

      return mappedRelationsContent as T;
    };

/**
 * Fetches and maps the relations for contents.
 *
 * @param core - The XcoreCore instance used for fetching content.
 * @param relationSourceFields - The values fields on the content that represent unfetched relations.
 * If the target relation field is in list object this field needs to be specified in `ofListObjectField`.
 * @param options - Optional parameters for fetching content extending from `GetMixedContentsOptions`.
 * @param subRelations - Optional recursive fetch settings for deeper nested relations.
 *
 * @returns A function that fetches the relations for a given contents, or returns `null` if the content is `null`.
 */
const fetchRelationsForContents =
  ({ core, relationSourceFields, options, subRelations }: FetchContentRelationsProps) =>
    async (contents: Content[] | null): Promise<Content[] | null> => {
      if (!contents || contents.length === 0) {
        return contents;
      }

      let relationsIdsObjects: ContentUnfetchedValuesFieldsObjectResolved[] = [];

      contents.forEach((content) => {
        relationsIdsObjects = [
          ...relationsIdsObjects,
          ...resolveContentUnfetchedValuesFieldsIds({
            content,
            fields: relationSourceFields,
          }),
        ];
      });

      const uniqueRelationsIds = [...new Set(relationsIdsObjects.map(({ id }) => id))];

      const fetchedRelationsContentsObject = await core.content.listByIds(uniqueRelationsIds, { ...options, includeNonLocalized: true });
      let fetchedRelationsContents: Content[] = fetchedRelationsContentsObject.contents;

      if (subRelations) {
        const fetchedRelationsContentsSubRelations = await fetchRelationsForContents({ core, ...subRelations })(fetchedRelationsContents);
        if (fetchedRelationsContentsSubRelations) {
          fetchedRelationsContents = fetchedRelationsContentsSubRelations;
        }
      }

      const mappedRelationsContents = contents.map(
        mapFetchedContentRelationsToContent({
          fetchedRelationsContents,
          relationSourceFields,
        })
      );

      return mappedRelationsContents;
    };

/**
 * Fetches and maps the relations for contents fetched with `listByIds` or `listByTypes` queries.
 *
 * @param core - The XcoreCore instance used for fetching content.
 * @param relationSourceFields - The values fields on the content that represent unfetched relations.
 * If the target relation field is in list object this field needs to be specified in `ofListObjectField`.
 * @param options - Optional parameters for fetching content extending from `GetMixedContentsOptions`.
 * @param subRelations - Optional recursive fetch settings for deeper nested relations.
 *
 * @returns A response function that fetches the relations for a given contents, so it can be easily used in `then` of get mixed contents functions, or returns `null` if the content is `null`.
 */
export const fetchRelationsForMixedContents =
  <T extends Content, TFields extends object = object>(props: FetchContentRelationsProps) =>
    async (res: MixedContentsResponse<TFields, T>): Promise<MixedContentsResponse<TFields, T>> => {
      return {
        ...res,
        contents: ((await fetchRelationsForContents(props)(res.contents as Content[])) ?? []) as (Omit<T, "values"> & { values: TFields })[],
      };
    };

/**
 * Fetches and maps the relations for contents fetched with `multipleByIds` or `multipleByType` queries.
 *
 * @param core - The XcoreCore instance used for fetching content.
 * @param relationSourceFields - The values fields on the content that represent unfetched relations.
 * If the target relation field is in list object this field needs to be specified in `ofListObjectField`.
 * @param options - Optional parameters for fetching content extending from `GetMixedContentsOptions`.
 * @param subRelations - Optional recursive fetch settings for deeper nested relations.
 *
 * @returns A response function that fetches the relations for a given contents, so it can be easily used in `then` of get contents functions, or returns `null` if the content is `null`.
 */
export const fetchRelationsForMultipleContents =
  <T extends Content, TField extends keyof T["values"] & string = keyof T["values"] & string>(props: FetchContentRelationsProps) =>
    async (res: ContentsResponse<T, TField>): Promise<ContentsResponse<T, TField>> => {
      return {
        ...res,
        contents: ((await fetchRelationsForContents(props)(res.contents)) ?? []) as PickContentValues<T, TField>[],
      };
    };
